import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, CircularProgress, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import api from '../../utils/api';
import JsonViewer from '../common/JsonViewer';
import ImageThumbnail from '../common/ImageThumbnail';
import { detectImage, imageLabel } from '../../utils/imageValues';
import { RUN_STATUS } from '../../utils/constants';

// Table cells show at most this many characters (e.g. a large base64 blob or an
// object with a nested image); the JSON view has the full value.
export const CELL_TEXT_LIMIT = 500;

export const cellText = (value) => {
  if (value === null || value === undefined) return '';
  let text;
  if (typeof value === 'object') {
    try { text = JSON.stringify(value); } catch { text = String(value); }
  } else {
    text = String(value);
  }
  return text.length > CELL_TEXT_LIMIT ? `${text.slice(0, CELL_TEXT_LIMIT)}…` : text;
};

const SampleTable = ({ columns, rows }) => {
  // One pass per sample: detected images and capped text for every cell.
  const cells = useMemo(() => rows.map((row) => columns.map((col) => {
    const image = detectImage(col, row?.[col], row);
    return image ? { image } : { text: cellText(row?.[col]) };
  })), [rows, columns]);
  return (
  <TableContainer sx={{ maxHeight: 400, border: 1, borderColor: 'divider', borderRadius: 1 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell key={col} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{col}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i} hover>
            {columns.map((col, c) => {
              const { image, text } = cells[i][c];
              if (image) {
                return (
                  <TableCell key={col} title={imageLabel(image)} sx={{ fontSize: '0.75rem', verticalAlign: 'top' }}>
                    <ImageThumbnail image={image} maxHeight={64} alt={`${col} image`} />
                  </TableCell>
                );
              }
              return (
                <TableCell key={col} title={text}
                  sx={{ fontSize: '0.75rem', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {text}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

const Message = ({ children }) => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{children}</Typography>
);

/**
 * What one node received (Input) and produced (Output) in a run, read from
 * GET /runs/{runId}/nodes/{nodeId}/preview. The worker stores a sample of up to
 * 50 records per side when the node finishes.
 */
const DataPreview = ({ runId, nodeId, nodeType, status, hasData, storageOff }) => {
  const [direction, setDirection] = useState(nodeType === 'input' ? 'output' : 'input');
  const [view, setView] = useState('json');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hasData) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    api.get(`/runs/${runId}/nodes/${nodeId}/preview`, { params: { direction } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setError('Could not load step data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId, nodeId, direction, hasData]);

  if (status === RUN_STATUS.PENDING || status === RUN_STATUS.RUNNING) {
    return <Message>This node is still running; its step data appears when it finishes.</Message>;
  }
  if (!hasData) {
    return storageOff
      ? <Alert severity="info">Step data storage is off for this pipeline. To see each step&apos;s input and output, open the pipeline, go to Settings, switch on "Store step data for debugging", and run it again.</Alert>
      : <Message>No step data was stored for this node.</Message>;
  }

  const rows = data?.rows || [];
  const tableable = (data?.columns || []).length > 0;
  let body;
  if (loading || !data) {
    body = <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>;
  } else if (direction === 'input' && nodeType === 'input') {
    body = <Message>Input nodes have no input; see Output.</Message>;
  } else {
    body = (
      <>
        {data.note && <Alert severity="warning" sx={{ mb: 1 }}>{data.note}</Alert>}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            {data.truncated ? `Showing first ${rows.length} of ${data.total}` : `${data.total} record${data.total === 1 ? '' : 's'}`}
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={tableable ? view : 'json'}
            onChange={(_, v) => v && setView(v)} aria-label="Step data view">
            <ToggleButton value="json">JSON</ToggleButton>
            <ToggleButton value="table" disabled={!tableable}>Table</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {rows.length === 0 && !data.note ? <Message>No records.</Message>
          : view === 'table' && tableable ? <SampleTable columns={data.columns} rows={rows} />
            : <JsonViewer value={rows} defaultExpandDepth={2} />}
      </>
    );
  }

  return (
    <Box>
      <Tabs value={direction} onChange={(_, v) => setDirection(v)} sx={{ mb: 1, minHeight: 36 }}>
        <Tab value="input" label="Input" sx={{ minHeight: 36 }} />
        <Tab value="output" label="Output" sx={{ minHeight: 36 }} />
      </Tabs>
      {error ? <Alert severity="error">{error}</Alert> : body}
    </Box>
  );
};

export default DataPreview;
